import React, { useState } from 'react';
import { Calendar, Clock, Star, Play } from 'lucide-react';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Projects } from '../data/Data';

const ProjectsSection = () => {
  const [hoveredProject, setHoveredProject] = useState(null);

  return (
    <section id="projects" className="py-20 bg-black relative">
      {/* Section Header */}
      <div className="container mx-auto px-6 mb-16">
        <div className="text-center">
          <h2 className="text-5xl md:text-6xl font-black mb-4">
            <span className="bg-gradient-to-r from-amber-400 to-amber-600 bg-clip-text text-transparent">
              FILMOGRAPHY
            </span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            A collection of blockbuster projects i have worked on.
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-red-500 mx-auto mt-6"></div>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {Projects.map((project) => (
            <Card 
              key={project.id}
              className="relative overflow-hidden bg-gray-900 border-gray-700 hover:border-amber-500/50 transition-all duration-500 transform hover:scale-105 cursor-pointer group"
              onMouseEnter={() => setHoveredProject(project.id)}
              onMouseLeave={() => setHoveredProject(null)}
            >
              <CardContent className="p-0 relative h-96">
                {/* Movie Poster Background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${project.color} opacity-90`}>
                  <div className="absolute inset-0 bg-black/30"></div>
                </div>

                {/* Project Icon/Image */}
                <div className="absolute top-1/3 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <div className="text-8xl opacity-30 group-hover:opacity-50 transition-opacity duration-300">
                    {project.image}
                  </div>
                </div>

                {/* Movie Info Overlay */}
                <div className="absolute inset-0 p-6 flex flex-col justify-between">
                  {/* Top Section - Rating & Year */}
                  <div className="flex justify-between items-start">
                    <Badge variant="secondary" className="bg-black/70 text-white border-none">
                      {project.rating}
                    </Badge>
                    <div className="text-right">
                      <p className="text-white font-bold text-lg">{project.year}</p>
                      <p className="text-gray-300 text-sm flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {project.duration}
                      </p>
                    </div>
                  </div>

                  {/* Bottom Section - Title & Credits */}
                  <div>
                    <div className="mb-3">
                      <p className="text-amber-400 text-sm font-semibold tracking-wider mb-1">
                        {project.genre.toUpperCase()}
                      </p>
                      <h3 className={`text-2xl font-black ${project.textColor} leading-tight`}>
                        {project.title}
                      </h3>
                    </div>
                    
                    <p className="text-xs text-gray-300 mb-2">
                      Directed by {project.director}
                    </p>

                    {/* Star Rating */}
                    <div className="flex items-center space-x-1 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-amber-400 fill-current" />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Hover Trailer Effect */}
                <div className={`absolute inset-0 bg-black/90 flex items-center justify-center transition-opacity duration-500 ${
                  hoveredProject === project.id ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}>
                  <div className="text-center p-6">
                    <Play className="w-16 h-16 text-amber-500 mx-auto mb-4" />
                    <h4 className="text-white font-bold text-xl mb-3">{project.title}</h4>
                    <p className="text-gray-300 text-sm mb-4 leading-relaxed">
                      {project.description}
                    </p>
                    
                    {/* Technologies */}
                    <div className="space-y-2">
                      <p className="text-amber-400 text-xs font-semibold tracking-wider">STARRING:</p>
                      <div className="flex flex-wrap gap-2 justify-center">
                        {project.technologies.map((tech, index) => (
                          <Badge 
                            key={index}
                            variant="outline"
                            className="bg-amber-500/20 text-amber-300 border-amber-500/50 text-xs"
                          >
                            {tech}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <a href= {project.githubLink} target='blank'>
                      <button className="mt-4 bg-amber-600 hover:bg-amber-700 text-black font-semibold px-6 py-2 rounded-md transition-colors duration-300">
                        View Project
                      </button>
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Coming Soon Section */}
      {/* <div className="container mx-auto px-6 mt-16">
        <div className="text-center">
          <h3 className="text-3xl font-bold text-white mb-4">Coming Soon...</h3>
          <p className="text-gray-400 mb-8">More blockbuster projects in development</p>
          <div className="flex justify-center space-x-4">
            <div className="w-32 h-48 bg-gray-800 border-2 border-dashed border-gray-600 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <Calendar className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">Coming 2024</p>
              </div>
            </div>
            <div className="w-32 h-48 bg-gray-800 border-2 border-dashed border-gray-600 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <Calendar className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">In Production</p>
              </div>
            </div>
          </div>
        </div>
      </div> */}
    </section>
  );
};

export default ProjectsSection;